@if (!isLoading) {
  <div class="flex flex--column gap-7 p-6">
    <ep-breadcrumbs>
      <ep-breadcrumb [routerLink]="['/']"><span>Realm</span></ep-breadcrumb>
      <ep-breadcrumb><span>Realm attributes</span></ep-breadcrumb>
    </ep-breadcrumbs>
    <h2>Azure Group Synchronization Settings</h2>

    @if (successMessage) {
      <ep-alert type="toast" variant="alt" color="success" [center]="true" [bottom]="true">
        <span slot="header">{{ successMessage }}</span>
      </ep-alert>
    }
    @if (errorMessage) {
      <ep-alert type="toast" variant="alt" color="danger" [center]="true" [bottom]="true">
        <span slot="header">{{ errorMessage }}</span>
      </ep-alert>
    }
    <form [formGroup]="realmForm" (ngSubmit)="saveRealmAttributes()" class="flex flex-col gap-6">
      <div class="flex flex-col gap-5" formGroupName="azureGroupSync">
        <h3>Azure Group Sync</h3>
        <app-toggleable-option formControlName="enabled">
          <span>Enable Azure Group Sync</span>
          <p ngProjectAs="description">
            Synchronizes groups from Azure Entra to Keycloak on login and at regular intervals.
          </p>
        </app-toggleable-option>
        <div class="flex flex-col gap-5">
          <ep-textfield
            label="Client ID"
            formControlName="client-id"
            [required]="true"
            ngDefaultControl
          >
            @if (realmForm.get('client-id')?.invalid && realmForm.get('client-id')?.touched) {
              <div slot="error">Client ID is required</div>
            }
          </ep-textfield>
          <ep-textfield
            label="Client Secret"
            formControlName="client-secret"
            [inputType]="showClientSecret() ? 'text' : 'password'"
            [required]="true"
            ngDefaultControl
          >
            <ep-button
              size="small"
              slot="suffix"
              (click)="toggleClientSecretVisibility()"
              type="quiet"
            >
              @if (showClientSecret()) {
                <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="m644-428-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428Zm128 126-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302Zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56ZM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82Zm319 93Zm-151 75Z"
                  />
                </svg>
              } @else {
                <svg class="icon icon--small" viewBox="0 -960 960 960" fill="currentColor">
                  <path
                    d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392Zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200Zm0-300Zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280Z"
                  />
                </svg>
              }
            </ep-button>
            @if (
              realmForm.get('client-secret')?.invalid && realmForm.get('client-secret')?.touched
            ) {
              <div slot="error">Client Secret is required</div>
            }
          </ep-textfield>
          <ep-textfield
            label="Tenant ID"
            formControlName="tenant-id"
            [required]="true"
            ngDefaultControl
          >
            @if (realmForm.get('tenant-id')?.invalid && realmForm.get('tenant-id')?.touched) {
              <div slot="error">Tenant ID is required</div>
            }
          </ep-textfield>
          <ep-textfield label="Parent Group" formControlName="parent-group" ngDefaultControl>
          </ep-textfield>
        </div>
      </div>
      <div class="flex justify-end items-center gap-4">
        <ep-button type="button" (click)="loadRealmData()" [disabled]="isSaving">
          <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
            <path
              d="M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z"
            />
          </svg>
          <span>Refresh</span>
        </ep-button>
        <ep-button
          color="primary"
          type="submit"
          (click)="saveRealmAttributes()"
          [disabled]="isSaving"
        >
          <svg class="icon" viewBox="0 -960 960 960" fill="currentColor">
            <path
              d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"
            />
          </svg>
          <span>Save Changes</span>
        </ep-button>
      </div>
    </form>
  </div>
}
@if (isLoading) {
  <div class="loading-indicator">Loading realm settings...</div>
}
